import React, { useState, useEffect } from 'react';
import {
  Monitor,
  Plus,
  Edit,
  Trash2,
  Copy,
  ExternalLink,
  Eye,
  Settings,
  Save,
  X
} from 'lucide-react';
import api from '../services/api';

export default function ScreensManager() {
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    filters: {
      status: [],
      priority: [],
      department: '',
      showResolved: false
    },
    settings: {
      refreshInterval: 30,
      fontSize: 'normal',
      theme: 'dark',
      layout: 'grid'
    }
  });

  useEffect(() => {
    loadScreens();
  }, []);

  const loadScreens = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/screens');
      setScreens(response.data.screens || []);
    } catch (error) {
      console.error('Error cargando pantallas:', error);
      // Mock data para desarrollo
      setScreens([
        {
          id: '1',
          name: 'Taller Principal',
          description: 'Pantalla del taller con todas las averías abiertas',
          url: '/pantallas?screen=1',
          filters: { status: ['abierto', 'en_progreso'], priority: [] },
          settings: { theme: 'dark', refreshInterval: 30 }
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    try {
      if (editingScreen) {
        await api.put(`/api/screens/${editingScreen.id}`, formData);
      } else {
        await api.post('/api/screens', formData);
      }
      loadScreens();
      closeModal();
    } catch (error) {
      console.error('Error guardando pantalla:', error);
    }
  };

  const handleDelete = async (screenId) => {
    if (!window.confirm('¿Eliminar esta pantalla?')) return;
    try {
      await api.delete(`/api/screens/${screenId}`);
      loadScreens();
    } catch (error) {
      console.error('Error eliminando pantalla:', error);
    }
  };

  const openModal = (screen = null) => {
    if (screen) {
      setEditingScreen(screen);
      setFormData({
        name: screen.name,
        description: screen.description || '',
        filters: screen.filters || { status: [], priority: [], department: '', showResolved: false },
        settings: screen.settings || { refreshInterval: 30, fontSize: 'normal', theme: 'dark', layout: 'grid' }
      });
    } else {
      setEditingScreen(null);
      setFormData({
        name: '',
        description: '',
        filters: { status: ['abierto', 'en_progreso'], priority: [], department: '', showResolved: false },
        settings: { refreshInterval: 30, fontSize: 'normal', theme: 'dark', layout: 'grid' }
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingScreen(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(window.location.origin + text);
    alert('URL copiada al portapapeles');
  };

  const getScreenUrl = (screen) => {
    return `/pantallas?screen=${screen.id}`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Monitor className="w-7 h-7" />
            Pantallas de Incidencias
          </h2>
          <p className="text-gray-600 mt-1">
            Crea pantallas personalizadas para mostrar en monitores del taller/oficina
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Nueva Pantalla
        </button>
      </div>

      {/* Lista de pantallas */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando...</div>
        </div>
      ) : screens.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Monitor className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">No hay pantallas configuradas</p>
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Crear primera pantalla
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {screens.map((screen) => (
            <div key={screen.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-800">{screen.name}</h3>
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs font-mono rounded">
                      PIN: {screen.pin_code || '------'}
                    </span>
                  </div>
                  {screen.description && (
                    <p className="text-gray-600 text-sm mb-3">{screen.description}</p>
                  )}

                  <div className="flex flex-wrap gap-2 mb-3">
                    {/* Filtros activos */}
                    {screen.filters?.status?.length > 0 && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        Estados: {screen.filters.status.join(', ')}
                      </span>
                    )}
                    {screen.filters?.priority?.length > 0 && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                        Prioridades: {screen.filters.priority.join(', ')}
                      </span>
                    )}
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      Actualiza cada {screen.settings?.refreshInterval || 30}s
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      Tema {screen.settings?.theme || 'dark'}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="font-medium">URL directa:</span>
                      <code className="px-2 py-1 bg-gray-100 rounded text-xs flex-1">
                        {window.location.origin + getScreenUrl(screen)}
                      </code>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="font-medium">Para dispositivos:</span>
                      <code className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-mono font-bold">
                        {window.location.origin}/display/{screen.pin_code || '------'}
                      </code>
                      <span className="text-xs text-gray-400">(Raspberry Pi, TV, etc.)</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => window.open(getScreenUrl(screen), '_blank')}
                    className="p-2 hover:bg-gray-100 rounded"
                    title="Abrir en nueva pestaña"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => copyToClipboard(getScreenUrl(screen))}
                    className="p-2 hover:bg-gray-100 rounded"
                    title="Copiar URL"
                  >
                    <Copy className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => openModal(screen)}
                    className="p-2 hover:bg-gray-100 rounded"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(screen.id)}
                    className="p-2 hover:bg-gray-100 rounded"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {editingScreen ? 'Editar Pantalla' : 'Nueva Pantalla'}
              </h3>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Información básica */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la pantalla *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Taller Principal"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción opcional"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              {/* Filtros */}
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Filtros de Incidencias</h4>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Estados a mostrar</label>
                    <div className="flex flex-wrap gap-2">
                      {['abierto', 'asignado', 'en_progreso', 'resuelto', 'cerrado'].map(status => (
                        <label key={status} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.filters.status.includes(status)}
                            onChange={(e) => {
                              const newStatus = e.target.checked
                                ? [...formData.filters.status, status]
                                : formData.filters.status.filter(s => s !== status);
                              setFormData({
                                ...formData,
                                filters: { ...formData.filters, status: newStatus }
                              });
                            }}
                            className="rounded"
                          />
                          <span className="text-sm capitalize">{status.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Prioridades</label>
                    <div className="flex flex-wrap gap-2">
                      {['critica', 'alta', 'media', 'baja'].map(priority => (
                        <label key={priority} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.filters.priority.includes(priority)}
                            onChange={(e) => {
                              const newPriority = e.target.checked
                                ? [...formData.filters.priority, priority]
                                : formData.filters.priority.filter(p => p !== priority);
                              setFormData({
                                ...formData,
                                filters: { ...formData.filters, priority: newPriority }
                              });
                            }}
                            className="rounded"
                          />
                          <span className="text-sm capitalize">{priority}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuración de pantalla */}
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Configuración de Pantalla</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Actualizar cada</label>
                    <select
                      value={formData.settings.refreshInterval}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, refreshInterval: parseInt(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value={10}>10 segundos</option>
                      <option value={30}>30 segundos</option>
                      <option value={60}>1 minuto</option>
                      <option value={300}>5 minutos</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Tamaño de texto</label>
                    <select
                      value={formData.settings.fontSize}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, fontSize: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="small">Pequeño</option>
                      <option value="normal">Normal</option>
                      <option value="large">Grande</option>
                      <option value="xlarge">Muy grande</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Tema</label>
                    <select
                      value={formData.settings.theme}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, theme: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="dark">Oscuro</option>
                      <option value="light">Claro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Diseño</label>
                    <select
                      value={formData.settings.layout}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, layout: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="grid">Cuadrícula</option>
                      <option value="list">Lista</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateOrUpdate}
                disabled={!formData.name.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {editingScreen ? 'Guardar Cambios' : 'Crear Pantalla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
